import { Divider, Table, Thead, Tbody, Heading, Collapse, IconButton } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { IconDownArrow, IconUpArrow } from 'assets/icons';
import { EmptyTableRow } from 'components/Table';
import ReactTablePagination from 'components/Table/ReactTablePagination';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableDataCell, ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';

interface IScheduleReportByUserTableProps {
  data: any;
  dateRange: any[];
  attributes: any;
  integrations: any;
  is_job_report: boolean;
}

const initialReportsByDateRangeTableQuery = {
  current_page: 1,
  first_page_url: '',
  from: 0,
  last_page: 0,
  last_page_url: '',
  next_page_url: '',
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
};

const ScheduleReportByUserTable: React.FC<IScheduleReportByUserTableProps> = ({
  data,
  dateRange,
  attributes,
  integrations,
  is_job_report,
}) => {
  const { t } = useTranslation(['common', 'reports']);

  const [paginationQuery, setPaginationQuery] = useState(initialReportsByDateRangeTableQuery);
  const handlePageChange = useCallback((selectedPageIndex: number) => {
    setPaginationQuery(prev => ({ ...prev, current_page: selectedPageIndex + 1 }));
  }, []);

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationQuery(prev => ({ ...prev, current_page: 1, per_page: pageSize }));
  };

  const formattedData = Object.keys(data || {})
    .map(id => {
      const currData = data[id];
      return [
        ...(Array.isArray(currData?.schedules) &&
          currData?.schedules.map((schedule: any) => ({
            ...schedule,
            id: id,
            name_: currData?.name,
          }))),
        { ...currData?.total, name: currData?.name, id: id, isTotal: true },
      ];
    })
    .flat();

  const getPaginatedData = useCallback(() => {
    const firstIndex = (paginationQuery.current_page - 1) * paginationQuery.per_page;
    const secondIndex = paginationQuery.current_page * paginationQuery.per_page;
    return formattedData.slice(firstIndex, secondIndex);
  }, [paginationQuery, formattedData]);

  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});
  const toggleExpand = (id: number) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const currentItems = getPaginatedData() || [];

  return (
    <div>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {is_job_report ? t('common:job') : t('common:user')} - {t('reports:searchResults')}
      </Heading>
      {attributes && (
        <ExportMenu
          dateRange={dateRange}
          attributes={attributes}
          displaySettings={integrations}
          keyAttribute={is_job_report}
        />
      )}
      <ScrollSync>
        <div style={{ position: 'relative' }}>
          <Table display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                max-width="100%"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="1">
                  <ReportsTableHeadCell width="250px">
                    {is_job_report ? t('common:job') : t('common:user')}
                  </ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">
                    {is_job_report ? t('common:user') : t('common:job')}
                  </ReportsTableHeadCell>
                  <ReportsTableHeadCell width="250px">{t('common:duration')}</ReportsTableHeadCell>
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>

            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {currentItems.length > 0 ? (
                  currentItems.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                      {/* Total Data */}
                      {item?.isTotal ? (
                        <ReportsTableRow
                          bg="primary.50"
                          minWidth="100%"
                          width="fit-content"
                          border="1"
                          onClick={() => toggleExpand(item?.id)}
                          cursor="pointer"
                        >
                          <ReportsTableDataCell fontWeight="semibold">
                            <>
                              <IconButton
                                aria-label="back"
                                variant="ghost"
                                cursor="pointer"
                                size="md"
                                sx={{ '& path': { fill: 'black' } }}
                              >
                                {expandedRows[item?.id] ? <IconUpArrow /> : <IconDownArrow />}
                              </IconButton>
                              {item?.name}
                            </>
                          </ReportsTableDataCell>
                          <ReportsTableDataCell>{t('common:totalTimeScheduled')}</ReportsTableDataCell>
                          <ReportsTableDataCell fontWeight="semibold">{item?.hours}</ReportsTableDataCell>
                        </ReportsTableRow>
                      ) : (
                        <Collapse in={expandedRows[item?.id]} animateOpacity>
                          <ReportsTableRow key={item?.id} width="fit-content" minWidth="100%" border="1">
                            <ReportsTableDataCell>
                              {index === 0 || (index > 0 && currentItems[index - 1]?.isTotal) ? item?.name_ : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell>{item?.name || ''}</ReportsTableDataCell>
                            <ReportsTableDataCell>{item?.total_hours || 0}</ReportsTableDataCell>
                          </ReportsTableRow>
                        </Collapse>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <EmptyTableRow />
                )}
              </Tbody>
            </ScrollSyncPane>
          </Table>
        </div>
      </ScrollSync>
      <ReactTablePagination
        pageCount={data ? Math.ceil(formattedData.length / paginationQuery.per_page) : 1}
        onPageChange={handlePageChange}
        currentPage={paginationQuery.current_page - 1}
        totalItems={formattedData.length || 0}
        rowsPerPage={paginationQuery.per_page || 10}
        handlePageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default ScheduleReportByUserTable;
